import { useState, useEffect, useCallback } from 'preact/hooks';
import Modal from './shared/Modal.jsx';
import Button from './shared/Button.jsx';

// De koppelingen van één debiteur: onder welke sleutel kent elk product deze
// klant? Daarmee telt 4U de gebruikers per klant.
//
// Een bestaande sleutel is hier bewust niet te wijzigen. Hem aanpassen laat het
// getelde aantal verspringen zonder dat er iets misgaat wat je ziet, en dat
// aantal komt op een factuur. Corrigeren hoort aan beide kanten tegelijk.
export default function DebtorProductsModal({ isOpen, debtor, onClose, onChanged }) {
  const [links, setLinks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState({ product_id: '', account_ref: '' });

  const refresh = useCallback(async () => {
    if (!debtor) return;
    setLoading(true);
    setFailed(false);
    try {
      const [l, p] = await Promise.all([
        fetch(`/api/debtor-products?debtorId=${debtor.id}`, { credentials: 'same-origin' }),
        fetch('/api/products', { credentials: 'same-origin' })
      ]);
      if (!l.ok || !p.ok) throw new Error('load_failed');
      setLinks((await l.json()).links || []);
      setProducts((await p.json()).products || []);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [debtor]);

  useEffect(() => { if (isOpen) refresh(); }, [isOpen, refresh]);

  const gekoppeld = new Set(links.map(l => l.productId));
  const beschikbaar = products.filter(p => !gekoppeld.has(p.id));

  async function add(e) {
    e.preventDefault();
    setError(null);
    const res = await fetch('/api/debtor-products', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        debtor_id: debtor.id,
        product_id: Number(adding.product_id),
        account_ref: adding.account_ref.trim()
      })
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(
        d.error === 'account_ref_taken'
          ? 'Die sleutel is al aan een andere debiteur gekoppeld bij dit product.'
          : d.error === 'link_exists'
            ? 'Deze debiteur is al aan dit product gekoppeld.'
            : 'Koppelen is niet gelukt.'
      );
      return;
    }
    setAdding({ product_id: '', account_ref: '' });
    await refresh();
    onChanged?.();
  }

  async function remove(link) {
    setError(null);
    if (!confirm(`Koppeling met ${link.productName} verwijderen?`)) return;
    const res = await fetch(`/api/debtor-products/${link.id}`, {
      method: 'DELETE', credentials: 'same-origin'
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      setError(
        d.error === 'link_in_use'
          ? 'Er loopt nog een abonnement op deze koppeling. Zeg dat eerst op.'
          : d.error === 'link_has_history'
            ? 'Aan deze koppeling hangt facturatiehistorie. Die blijft staan.'
            : 'Verwijderen is niet gelukt.'
      );
      return;
    }
    await refresh();
    onChanged?.();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Koppelingen van ${debtor?.name || ''}`}>
      {error && <div class="notice notice-error">{error}</div>}

      {loading ? (
        <div class="empty-state muted">Laden…</div>
      ) : failed ? (
        <div class="empty-state muted">Koppelingen konden niet worden geladen.</div>
      ) : links.length === 0 ? (
        <div class="empty-state">
          <p>Deze debiteur is nog aan geen enkel product gekoppeld.</p>
          <p class="muted">Zonder koppeling telt 4U geen gebruikers en kan er niet gefactureerd worden.</p>
        </div>
      ) : (
        <table class="data-table">
          <thead>
            <tr><th>Product</th><th>Sleutel</th><th /></tr>
          </thead>
          <tbody>
            {links.map(l => (
              <tr key={l.id}>
                <td>{l.productName}</td>
                <td>
                  {l.accountRef
                    ? <code>{l.accountRef}</code>
                    : <span class="muted">nog niet bekend</span>}
                </td>
                <td class="row-actions">
                  <Button variant="danger" size="sm" onClick={() => remove(l)}>Ontkoppelen</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {beschikbaar.length > 0 && (
        <form onSubmit={add} class="inline-form">
          <label>
            <span>Product</span>
            <select
              value={adding.product_id}
              onChange={e => setAdding({ ...adding, product_id: e.currentTarget.value })}
              required
            >
              <option value="">Kies een product…</option>
              {beschikbaar.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <label>
            <span>Sleutel in het product</span>
            <input
              value={adding.account_ref}
              onInput={e => setAdding({ ...adding, account_ref: e.currentTarget.value })}
              maxLength={120}
              placeholder="bv. sv-hoofddorp"
            />
            <span class="field-hint muted small">
              Hoofdlettergevoelig, precies zoals het product deze klant kent.
              Leeg laten mag als de klant daar nog niet bestaat; 4U telt dan niets
              en slaat de debiteur over bij het factureren.
            </span>
          </label>
          <Button type="submit" variant="primary">Koppelen</Button>
        </form>
      )}
    </Modal>
  );
}
